import React, { useState, useRef, useEffect, useCallback } from "react"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { ClaraTrendChart } from "./ClaraTrendChart"
import { ChartActions } from "./ChartActions"
import { ClaraGraphEmbed } from "./ClaraGraphEmbed"
import { ClaraPatientModal } from "./ClaraPatientModal"
import { displayOf, LABEL_STORE, readPatientLabels } from "./claraPatients"
import { conversationHistory, interpChip, rebuildTurns, viewLines, viewResult, viewSeries } from "./claraTurns"
import { ClaraOrb } from "../../Components/Clara/ClaraOrb"
import { ContextCards } from "../../Components/Clara/ContextCards"
import { StreamingAnswer } from "../../Components/Clara/StreamingAnswer"
import { boldFigures } from "../../Components/Clara/figures"
import { fetchRecordingTimeZone, useRecordingTimeZone } from "./recordingTime"
import { LongAnswer } from "./LongAnswer"
import { CLARA_TIMEOUT_MESSAGE, fetchWithTimeout, isTimeout } from "./claraFetch"
import { useEndpointProvider } from "../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider"
import styles from "./Clara.module.css"

// One id per chat session, sent with every question so the audit trail groups a whole
// conversation. New on "new chat" and reopened when resuming a saved one.
function mintConversationId() {
	return typeof crypto !== "undefined" && crypto.randomUUID
		? crypto.randomUUID()
		: `clara-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// The full base URL of Clara's API, prefix included. Deployments set
// REACT_APP_CLARA_API_URL in env-config.js (same mechanism as REACT_APP_IAM_ORIGIN_URL);
// the convention is path routing on the dashboard's own host, e.g.
// "https://<dashboard host>/clara/v1", which the ingress rewrites to Clara's internal
// /v1/clara routes. No new domain or certificate per environment that way. Without the
// variable Clara is not configured, and nothing is sent anywhere.
const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

// Max characters for a single message (shown as a live count, enforced on the input).
const MAX_CHARS = 300
const SERIES_HEIGHT = 200

// Mic input is 8-bit samples centred on 128, so this is the peak deviation from silence
// we require before treating a clip as containing speech. Below it, the clip is silence
// and we never transcribe it: a speech model handed silence hallucinates (it echoes its
// priming prompt), which would otherwise be auto-sent as a question nobody asked.
const SPEECH_PEAK_THRESHOLD = 10

// The latest answer renders the real embedded Data Review graph; if it errors for
// any reason (a patient/window the embed can't handle), this boundary catches it and
// falls back to the lightweight SVG so the chat never crashes.
class GraphErrorBoundary extends React.Component {
	state = { failed: false }
	static getDerivedStateFromError() {
		return { failed: true }
	}
	componentDidCatch(error) {
		// eslint-disable-next-line no-console
		console.warn("Clara embed failed; falling back to SVG chart:", error)
	}
	render() {
		return this.state.failed ? this.props.fallback : this.props.children
	}
}

// Pull a patient reference out of an ordinary question, so "tell me the heart rate for
// Patient_2025_Aug_19" can BOTH select that patient and answer in one shot. A "Patient_…"
// token is a strong signal (if it matches nobody we say so); a loose "for patient X" is
// weak (if it matches nobody we just treat it as a normal question against the current
// patient). Returns { term, strong } or null.
function extractPatientRef(question) {
	const token = question.match(/\bPatient_\w+/i)
	if (token) {
		return { term: token[0], strong: true }
	}
	// "…for (the) patient Clark Kent?" — a NAMED patient. Capture what trails "patient"
	// up to the end, then reject captures that are really a time/scope clause (e.g.
	// "for the patient in the past 4 hours") so those stay ordinary summary questions.
	const phrase = question.match(/\bfor\s+(?:the\s+)?patient\s+(.+?)\s*[?.!]*$/i)
	if (phrase) {
		const term = phrase[1].trim()
		const isTimeClause =
			/^(?:in|over|during|within|for|at|on|the|past|last|right|now)\b/i.test(term) ||
			/\b(?:hours?|mins?|minutes?|seconds?|days?)\b/i.test(term)
		if (term && !isTimeClause) {
			// A real name follows "for the patient" → strong: an unknown name should be
			// reported, not silently answered for whoever happens to be selected.
			return { term, strong: true }
		}
	}
	return null
}

/* The same recording, said briefly, for somewhere that has one line to say it.

   The full form leads with the archive's file name, which is right in the chat where
   Clara is telling you which of several files she opened, and wrong in a status bar,
   where PatientData_1769451614918930839 is 30 characters of nothing a clinician reads.
   The dates and the length are what they act on. */
function recordingSpan(recording, timeZone) {
	const start = Number(recording?.start)
	const end = Number(recording?.end)
	if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
		return ""
	}
	const at = new Date(start)
	const to = new Date(end)
	const zone = timeZone ? { timeZone } : {}
	const day = value => value.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric", ...zone })
	const clock = value => value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", ...zone })
	const minutes = Math.round((end - start) / 60000)
	const length =
		minutes < 60
			? `${minutes} min${minutes === 1 ? "" : "s"}`
			: minutes < 60 * 48
				? `${Math.round(minutes / 60)} hrs`
				: `${Math.round(minutes / (60 * 24))} days`
	const when =
		day(at) === day(to)
			? `${day(at)}, ${clock(at)} to ${clock(to)}`
			: `${day(at)} to ${day(to)}`
	return `${when} · ${length}`
}

// Small copy-to-clipboard control shown under each message bubble.
function CopyButton({ text }) {
	const [copied, setCopied] = useState(false)
	const copy = () => {
		if (!navigator.clipboard) {
			return
		}
		navigator.clipboard.writeText(text || "").then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1200)
		})
	}
	return (
		<button type="button" className={styles.copyButton} onClick={copy} aria-label="Copy" title="Copy">
			{copied ? (
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
			) : (
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
			)}
		</button>
	)
}

// Bold the headline numbers/durations so an answer is scannable at a glance.
function emphasize(text) {
	if (!text) {
		return text
	}
	const parts = text.split(/(-?\d+(?:\.\d+)?(?:\s?(?:minutes?|seconds?|hours?|mmHg|bpm|%))?)/gi)
	return parts.map((part, index) =>
		/^-?\d/.test(part) ? <strong key={index} className={styles.metric}>{part}</strong> : part
	)
}

// Reveals an answer word-by-word (typewriter). Pacing is capped so long answers
// don't crawl. `animate` is only true for the most recent answer.
function Typewriter({ text, animate, scrollRef }) {
	const [shown, setShown] = useState(animate ? "" : text)
	useEffect(() => {
		if (!animate) {
			setShown(text)
			return
		}
		setShown("")
		let i = 0
		const step = Math.max(1, Math.ceil(text.length / 70)) // ~70 ticks total, any length
		const id = setInterval(() => {
			i = Math.min(text.length, i + step)
			setShown(text.slice(0, i))
			// Follow the growing answer so it stays in view while typing. Instant (not
			// smooth) so it keeps pace with each tick. scrollRef is a stable ref.
			const el = scrollRef?.current
			if (el) {
				el.scrollTop = el.scrollHeight
			}
			if (i >= text.length) {
				clearInterval(id)
			}
		}, 16)
		return () => clearInterval(id)
		// scrollRef is a stable ref object — intentionally not a dependency.
	}, [text, animate])
	return <>{emphasize(shown)}</>
}


// The reference line(s) to draw: [low, high] for a range query, [threshold] for a
// single above/below query, [] otherwise.
function thresholdsOf(query) {
	if (!query) {
		return []
	}
	if (query.low != null && query.high != null) {
		return [query.low, query.high]
	}
	return query.threshold != null ? [query.threshold] : []
}

// Clara answers in one response, so there is no progress to report while she works.
// The steps she took arrive with the answer and are shown above it.
const WORKING_LABEL = "Working on it…"

// A new answer builds in place, top to bottom: its steps a quarter second apart, then the
// headline, then the text. The chart and the sources come PART_MS after the text is typed.
const STEP_MS = 250
const PART_MS = 300

export function revealDelays(message) {
	const steps = (message.steps || []).map((step, index) => index * STEP_MS)
	const afterSteps = steps.length ? steps[steps.length - 1] + PART_MS : 0
	const headline = message.headline || message.headlines?.length ? afterSteps : null
	const text = headline != null ? headline + PART_MS : afterSteps
	return { steps, headline, text }
}

function prefersReducedMotion() {
	return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

// Whether this answer draws a chart, by the same rules the chart block below uses. The
// "understood" chip becomes the chart's caption when it does.
function drawsChart(message, isLatest) {
	const live = isLatest && !message.historical && (message.result?.query || message.view?.series?.length > 1)
	return Boolean(live || viewSeries(message.view).length || (message.result ?? viewResult(message.view))?.trend?.length > 1)
}

// Suggested starters — all supported by the current intent (duration above/below
// a threshold over a window, optionally "for at least N minutes").

// The IANA zone this browser is displaying times in. Clara needs it to answer a question
// about a clock time, because the answer differs by an hour or more depending on the zone
// and nothing in the reply would reveal the mistake. Returns "" when the browser cannot
// say, which Clara treats as a reason to ask for a duration instead of guessing.
function clientTimezone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || ""
	} catch (error) {
		return ""
	}
}

// Reusable chat. Takes the patient context as props so the same component can be
// dropped into a Data Review modal later (where the patient is already known).
/* Remembering what a patient is CALLED, so the history rail can label a chat by the
   patient rather than by its first question. The audit trail stores the patient's key,
   not their name, and there is no endpoint that turns one key back into one patient. A
   small local map costs nothing and covers the case that matters: your own chats, on the
   machine you started them on. A name that survives to another machine needs a column on
   clara_audit, which is a migration and belongs in its own change. */

/* "PatientData_1769451614918930839, 26 Jan 2026 14:43 to 14:45, 2 mins" or "".

   The duration is the part that earns its place. Clara's own availability sentence says
   "The recording covers 26 Jan 2026" for an archive holding 2 minutes 44 seconds, which
   reads as a day of data, and every question that followed looked broken because of it.
   Times are rendered in the recording's timezone, the same way Data Review's axis is. */
function describeRecording(recording, timeZone) {
	if (!recording?.name) {
		return ""
	}
	const start = Number(recording.start)
	const end = Number(recording.end)
	if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
		return recording.name
	}
	const at = new Date(start)
	const to = new Date(end)
	const zone = timeZone ? { timeZone } : {}
	const day = value => value.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric", ...zone })
	const clock = value => value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", ...zone })
	const minutes = Math.round((end - start) / 60000)
	const length =
		minutes < 60
			? `${minutes} min${minutes === 1 ? "" : "s"}`
			: minutes < 60 * 48
				? `${Math.round(minutes / 60)} hrs`
				: `${Math.round(minutes / (60 * 24))} days`
	// A recording that crosses midnight has to say BOTH dates. Printing the start date
	// and then two clock times gave "Aug 4, 2026 07:28 AM to 11:01 PM, 16 days" for a
	// sixteen day archive, which reads as one long day and contradicts its own duration
	// in the same sentence.
	const when =
		day(at) === day(to)
			? `${day(at)} ${clock(at)} to ${clock(to)}`
			: `${day(at)} ${clock(at)} to ${day(to)} ${clock(to)}`
	return `${recording.name}, ${when}, ${length}`
}

function rememberLabel(patientKey, label) {
	if (patientKey == null || !label) {
		return
	}
	try {
		const labels = readPatientLabels()
		labels[String(patientKey)] = String(label)
		window.localStorage.setItem(LABEL_STORE, JSON.stringify(labels))
	} catch (error) {
		// A full or disabled localStorage costs a nicety, never an answer.
	}
}

export function ClaraChat({ patientId, dataObjectId, referenceEndMs, patientReady = false, patients = [], findPatients, selectedPatient, selectedRecording, onSelectPatient, onStart, onNewChat, onRailData }) {
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const recordingTimeZone = useRecordingTimeZone(dataObjectId)
	const [messages, setMessages] = useState([])
	const [conversations, setConversations] = useState([])
	// The rail replaces the New/Chats buttons. Collapsed state is remembered for the
	// session: a clinician who wants the conversation full width should not have to
	// say so after every question.
	// The rail lives on the PAGE, beside this component rather than inside it, so the
	// conversation's own scrolling and stacking contexts cannot clip it. Its data is
	// reported upward instead.
	const [activeConversationId, setActiveConversationId] = useState(null)
	const [loadingConversation, setLoadingConversation] = useState(false)
	const [input, setInput] = useState("")
	const [loading, setLoading] = useState(false)
	// Answers whose words have finished arriving. Sources hang off this so the
	// evidence appears after the statement it supports, not underneath a half
	// written sentence.
	const [streamedIds, setStreamedIds] = useState(() => new Set())
	const [recording, setRecording] = useState(false)
	const [transcribing, setTranscribing] = useState(false)
	const [pickerOpen, setPickerOpen] = useState(false)
	// A chat asks who it is about once, at the start. Answered or skipped, it does not
	// ask again: the picker reopening on its own mid conversation would be worse than
	// never opening at all.
	const [patientAsked, setPatientAsked] = useState(false)
	// In-flight marker for opening a saved chat. A ref rather than state because the
	// value is read inside a closure the rail keeps, where state goes stale.
	const openingRef = useRef(false)
	// The latest implementations, refreshed every render, plus the stable wrappers the
	// rail is given. See the rail effect below for why this indirection exists.
	const handlersRef = useRef({})
	const railHandlers = useRef({
		onNewChat: () => handlersRef.current.newChat?.(),
		onOpen: id => handlersRef.current.openConversation?.(id),
	}).current
	// "Talk back": when on, Clara speaks each answer aloud as well as writing it. Off by
	// default, since an ICU is not a place to start making noise unasked.
	const [speakAnswers, setSpeakAnswers] = useState(false)
	const endRef = useRef(null)
	const messagesRef = useRef(null)
	const mediaRecorderRef = useRef(null)
	const chunksRef = useRef([])
	const heardSpeechRef = useRef(false)
	const sendRef = useRef(null)
	// The active conversation id (sent with every question). activeConversationId mirrors
	// it for highlighting the row in the history rail.
	const conversationIdRef = useRef(null)
	if (!conversationIdRef.current) {
		conversationIdRef.current = mintConversationId()
	}

	// Voice out. The audio element currently playing, so a new answer can cut off one
	// still being spoken, and a sequence number so a SLOW synthesis can't talk over a
	// newer answer that came back first (ask two questions quickly and the responses can
	// land out of order).
	const audioRef = useRef(null)
	const speakSeqRef = useRef(0)

	const stopSpeaking = useCallback(() => {
		const audio = audioRef.current
		if (audio) {
			audio.pause()
			URL.revokeObjectURL(audio.src)
			audioRef.current = null
		}
	}, [])

	// Speak an answer aloud via Clara's /speak (OpenAI TTS). The browser's built in
	// speechSynthesis was on-device OS TTS: flat, and a different voice for every user.
	// Voice is a nicety, so every failure here is swallowed: Clara stays silent rather
	// than breaking the chat.
	const speak = useCallback(async (text) => {
		if (!text) {
			return
		}
		const mine = ++speakSeqRef.current
		stopSpeaking()
		try {
			const response = await fetch(`${CLARA_API}/speak`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				// workspace_name is not optional: Clara's require_auth reads it from the JSON
				// body to ask IAM whether this user may do this here. Omit it and IAM rejects
				// the request before any synthesis happens, which reads as the voice simply
				// not working.
				body: JSON.stringify({ text, workspace_name: selectedWorkspace }),
			})
			if (!response.ok) {
				console.warn(`Clara: /speak returned ${response.status}, staying silent.`)
				return
			}
			if (mine !== speakSeqRef.current) {
				return
			}
			const url = URL.createObjectURL(await response.blob())
			if (mine !== speakSeqRef.current) {
				URL.revokeObjectURL(url)
				return
			}
			const audio = new Audio(url)
			audioRef.current = audio
			audio.onended = () => {
				URL.revokeObjectURL(url)
				if (audioRef.current === audio) {
					audioRef.current = null
				}
			}
			await audio.play()
		} catch (error) {
			// Voice is a nicety: Clara stays silent rather than breaking the chat. But log
			// it, because a silent swallow here is indistinguishable from "the feature is
			// off" and sends you hunting in the wrong place.
			console.warn("Clara: voice output failed, continuing silently.", error)
		}
	}, [getToken, stopSpeaking, selectedWorkspace])

	// Don't keep talking after the user navigates away.
	useEffect(() => stopSpeaking, [stopSpeaking])

	// Load the user's saved conversations for the history rail.
	const fetchConversations = useCallback(async () => {
		if (!CLARA_API) return
		try {
			const response = await fetch(`${CLARA_API}/conversations`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace }),
			})
			const data = await response.json()
			setConversations(Array.isArray(data) ? data : [])
		} catch (error) {
			// History is best-effort; leave the list as-is.
		}
	}, [getToken, selectedWorkspace])

	useEffect(() => {
		fetchConversations()
	}, [fetchConversations])

	// The rail is always visible, so the list refreshes when it is expanded rather than
	// when a dropdown opens. The chat you were just in is saved on its last answered
	// turn, and needs to appear without a reload.
	useEffect(() => {
		fetchConversations()
	}, [fetchConversations])

	// Start a fresh conversation (empty chat, new id, back to the welcome screen).
	const newChat = () => {
		conversationIdRef.current = mintConversationId()
		setActiveConversationId(null)
		setMessages([])
		setInput("")
		setPatientAsked(false)
		onNewChat?.()
	}

	/* A chat belongs to ONE patient, and it says so before the first question.
	   Conversation 0337f4c0 in the audit trail holds seven turns across two patients: a
	   2017 archive for three turns, then a 2026 one. Read back afterwards, "then for what
	   window do you have it?" points at a different patient than the question three turns
	   above it. Clara also sends the last three turns as context with every question, so
	   after a switch that context describes someone else's numbers, and the anchor for
	   "yesterday" moves years. */
	useEffect(() => {
		if (patientAsked || loadingConversation) {
			return
		}
		if (!patientId && messages.length === 0) {
			setPickerOpen(true)
			setPatientAsked(true)
		}
	}, [patientAsked, patientId, messages.length, loadingConversation])

	/* Attaching a patient. Choosing a DIFFERENT one starts a new chat rather than
	   switching underneath the conversation, which keeps one patient per transcript
	   without ever refusing the click. */
	const attachPatient = async patient => {
		const key = patient?.patient_key ?? patient
		const switching = patientId && String(key) !== String(patientId)
		if (switching) {
			newChat()
		}
		setPatientAsked(true)
		rememberLabel(key, displayOf(patient))
		const resolved = (await onSelectPatient?.(patient)) || {}
		if (resolved.noReadableRecording) {
			onStart?.()
			setMessages([
				{
					who: "clara",
					text:
						`I can see ${displayOf(patient)} in the patient list, but the files for their ` +
						"recordings are not on this server, so I cannot read any measurements. " +
						"I can still answer from the uploaded documents.",
				},
			])
			return resolved
		}
		if (resolved.noRecording) {
			onStart?.()
			setMessages([
				{
					who: "clara",
					text: `${displayOf(patient)} has no recording to review. I can still answer from the uploaded documents.`,
				},
			])
			return resolved
		}
		// Name the archive and its real span. Which recording Clara reads decides what
		// every relative phrase in the conversation means, so it is said once, plainly,
		// rather than left for the clinician to infer from the answers.
		const described = describeRecording(
			resolved.recording,
			await fetchRecordingTimeZone(endpointProvider, LINKS, resolved.recording?.id),
		)
		// The conversation has begun, so retire the welcome overlay. It is absolutely
		// positioned over the transcript, and without this the first thing Clara says
		// would be written underneath it.
		onStart?.()
		setMessages(prev => [
			...prev,
			{
				who: "clara",
				text: described
					? `Now watching ${displayOf(patient)}, reading ${described}.`
					: `Now watching ${displayOf(patient)}.`,
			},
		])
		return resolved
	}

	// Reopen a saved conversation: rebuild the turns (text + interpretation chip), point
	// new messages at the same conversation id, and re-select its patient.
	const openConversation = async (conversationId) => {
		// The guard reads a REF, not the state. This function is handed to the rail from
		// inside an effect, so the rail holds whatever closure that effect last created,
		// and that closure captured `loadingConversation` at its own moment in time.
		// Opening a chat set it true, the effect re-ran while it was still true, and the
		// flag turning false again changed no dependency, so the rail was left holding a
		// closure that believed a load was permanently in progress. One chat switch
		// worked, every one after it returned here and did nothing.
		if (openingRef.current) {
			return
		}
		openingRef.current = true
		setLoadingConversation(true)
		try {
			const response = await fetch(`${CLARA_API}/conversation`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ conversation_id: conversationId, workspace_name: selectedWorkspace }),
			})
			const turns = await response.json()
			if (!Array.isArray(turns) || turns.length === 0) {
				return
			}
			// Rebuild the chat from the saved turns. The stored `chart` lets each answer
			// redraw its SVG; reconstruct a minimal result.query so thresholdsOf() finds
			// the reference lines. Marked `historical` so it uses the SVG, never the live
			// single-instance embed.
			const rebuilt = rebuildTurns(turns)
			conversationIdRef.current = conversationId
			setActiveConversationId(conversationId)
			setMessages(rebuilt)
			onStart?.()
			// Re-select the patient this conversation ended on, so follow-ups work.
			// The audit trail records the patient KEY, not the row, so this can only
			// re-select a patient we have already seen this session. Reopening a chat for
			// someone outside that set leaves the transcript readable and the patient
			// unselected, which is what it did before as well.
			const lastPatientId = [...turns].reverse().find(t => t.patient_id != null)?.patient_id
			if (lastPatientId != null) {
				onSelectPatient?.(lastPatientId)
			}
		} catch (error) {
			// Couldn't load; leave the current chat untouched.
		} finally {
			openingRef.current = false
			setLoadingConversation(false)
		}
	}

	// The newest answer, while it builds. Stage 0 fades in its steps and headline, 1 starts
	// the text, 2 is the text typed out, 3 brings the chart and the sources. Reopened chats,
	// older answers and reduced motion show everything at once.
	const reducedMotion = prefersReducedMotion()
	const newest = messages[messages.length - 1]
	const building = newest?.who === "clara" && !newest.historical && !reducedMotion ? newest : null
	const [reveal, setReveal] = useState({ message: null, stage: 0 })
	const stage = building && reveal.message === building ? reveal.stage : 0
	const shows = (message, part) => message !== building || stage >= part
	useEffect(() => {
		if (!building) {
			return undefined
		}
		if (reveal.message !== building) {
			setReveal({ message: building, stage: 0 })
			return undefined
		}
		const wait = reveal.stage === 0 ? revealDelays(building).text : reveal.stage === 2 ? PART_MS : null
		if (wait == null) {
			return undefined
		}
		const id = setTimeout(() => setReveal(prev => (prev.message === building ? { ...prev, stage: prev.stage + 1 } : prev)), wait)
		return () => clearTimeout(id)
	}, [building, reveal])

	// A question scrolls up to sit near the top, and a new answer brings its top into view
	// once. Nothing after that moves the list, so the text typing and the chart drawing
	// below never pull the reader away from the top of the answer. A reopened chat opens
	// at its end, as before.
	const scrolledRef = useRef(null)
	useEffect(() => {
		const el = messagesRef.current
		const last = messages[messages.length - 1]
		if (!el || !last || (last === scrolledRef.current && last.who !== "user")) {
			return
		}
		// A question and a reply added together (a patient chooser) scroll to the question.
		const previous = messages[messages.length - 2]
		const asked = last.who !== "user" && previous?.who === "user" && previous !== scrolledRef.current
		scrolledRef.current = last
		if (last.historical) {
			el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
			return
		}
		const row = el.children[messages.length - (asked ? 2 : 1)]
		if (!row) {
			return
		}
		// The list's 64px top and 220px bottom padding sit under the header and the composer.
		const rowTop = el.scrollTop + row.getBoundingClientRect().top - el.getBoundingClientRect().top
		if (!asked && last.who !== "user" && rowTop >= el.scrollTop + 64 && rowTop <= el.scrollTop + el.clientHeight - 220) {
			return
		}
		el.scrollTo({ top: rowTop - 72, behavior: reducedMotion ? "auto" : "smooth" })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messages, loading])

	// The chart comes last and lands below the fold, so the list follows it down until its
	// foot clears the composer, unless the reader scrolled up since asking. Not to the very
	// bottom: the newest turn is a screen tall, and that would push a short answer's text away.
	const heldRef = useRef(false)
	const hold = () => {
		heldRef.current = true
	}
	useEffect(() => {
		const el = messagesRef.current
		const figure = el?.children[messages.length - 1]?.querySelector("figure")
		if (!figure || heldRef.current || newest?.who !== "clara" || newest.historical || !shows(newest, 3) || !drawsChart(newest, true)) {
			return
		}
		const top = el.scrollTop + figure.getBoundingClientRect().bottom - el.getBoundingClientRect().top - (el.clientHeight - 220)
		if (top > el.scrollTop) {
			el.scrollTo({ top, behavior: reducedMotion ? "auto" : "smooth" })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newest, stage])

	// Push-to-talk: hold the orb to record, release to transcribe (clara's /transcribe,
	// gpt-4o-transcribe biased to clinical vocab) and AUTO-SEND the question. sendRef
	// keeps the freshest send() so the auto-send carries up-to-date context.
	const startRecording = async () => {
		if (recording || transcribing) {
			return
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			const recorder = new MediaRecorder(stream)
			chunksRef.current = []
			heardSpeechRef.current = false
			recorder.ondataavailable = event => {
				if (event.data.size) {
					chunksRef.current.push(event.data)
				}
			}

			// Watch the live input level so we know whether anything was actually said.
			let audioContext = null
			let levelTimer = null
			try {
				audioContext = new (window.AudioContext || window.webkitAudioContext)()
				const analyser = audioContext.createAnalyser()
				analyser.fftSize = 2048
				audioContext.createMediaStreamSource(stream).connect(analyser)
				const samples = new Uint8Array(analyser.fftSize)
				levelTimer = setInterval(() => {
					analyser.getByteTimeDomainData(samples)
					let peak = 0
					for (let i = 0; i < samples.length; i++) {
						const deviation = Math.abs(samples[i] - 128)
						if (deviation > peak) {
							peak = deviation
						}
					}
					if (peak > SPEECH_PEAK_THRESHOLD) {
						heardSpeechRef.current = true
					}
				}, 100)
			} catch (error) {
				// No AudioContext (older browser): fall back to transcribing every clip.
				heardSpeechRef.current = true
			}

			recorder.onstop = async () => {
				stream.getTracks().forEach(track => track.stop())
				if (levelTimer) {
					clearInterval(levelTimer)
				}
				if (audioContext) {
					audioContext.close().catch(() => {})
				}
				setRecording(false)
				const blob = new Blob(chunksRef.current, { type: "audio/webm" })
				// Nothing said: don't transcribe, don't send. (Transcribing silence makes the
				// model echo its priming prompt, which used to get auto-sent as a question.)
				if (!blob.size || !heardSpeechRef.current) {
					return
				}
				setTranscribing(true)
				try {
					const form = new FormData()
					form.append("audio", blob, "audio.webm")
					const response = await fetch(`${CLARA_API}/transcribe?workspace_name=${encodeURIComponent(selectedWorkspace)}`, {
						method: "POST",
						headers: { Authorization: getToken() },
						body: form,
					})
					const data = await response.json()
					if (data.transcript) {
						sendRef.current?.(data.transcript) // auto-send the transcribed question
					}
				} catch (error) {
					// Transcription failed — nothing sent; the user can type instead.
				} finally {
					setTranscribing(false)
				}
			}
			mediaRecorderRef.current = recorder
			recorder.start()
			setRecording(true)
		} catch (error) {
			// Mic permission denied / unavailable — nothing to do.
		}
	}

	const stopRecording = () => {
		if (mediaRecorderRef.current?.state === "recording") {
			mediaRecorderRef.current.stop()
		}
	}

	// Click to start, click again to stop → transcribe → auto-send.
	const toggleRecording = () => {
		if (recording) {
			stopRecording()
		} else {
			startRecording()
		}
	}

	// Match the term Clara extracted against the patient list. 1 → select; many → chooser.
	//
	// The match happens on the SERVER now. It used to filter a page of patients the
	// browser had prefetched, which meant "watch patient RR9331CO" answered "I couldn't
	// find a patient matching RR9331CO" for a patient who was admitted at the time and
	// findable from Data Visualize's own search a second later. A patient Clara cannot
	// see is worse than a slow answer.
	const resolvePatientSelection = async term => {
		const SHOWN = 12
		// The user may quote the name (watch "Patient_2017_Nov_28") and the parser can
		// pass those quotes through. They are never part of the displayed name.
		const cleaned = (term || "").trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, "").trim()
		if (!cleaned) {
			// Bare "watch patient": offer the list to click instead of a dead end.
			const first = patients.length ? patients : (await findPatients?.("")) || []
			if (!first.length) {
				return { text: "Which patient would you like to watch? I don't see any patients with recordings yet." }
			}
			const text =
				first.length > SHOWN
					? `Which patient would you like to watch? Here are the first ${SHOWN}, or give me a name.`
					: "Which patient would you like to watch?"
			return { text, candidates: first.slice(0, SHOWN) }
		}
		// The server matches the patient identifier, the site and the project (and the
		// name on clinical deployments). The local pass keeps matching the DISPLAYED
		// name, which is what the user is reading and what demo archives are named by.
		const needle = cleaned.toLowerCase()
		const found = (await findPatients?.(cleaned)) || []
		const byKey = new Map(found.map(patient => [String(patient.patient_key), patient]))
		patients
			.filter(patient => displayOf(patient).toLowerCase().includes(needle))
			.forEach(patient => byKey.set(String(patient.patient_key), patient))
		const matches = [...byKey.values()]
		if (matches.length === 0) {
			return { text: `I couldn't find a patient matching “${cleaned}”.` }
		}
		if (matches.length === 1) {
			return { text: `Now watching ${displayOf(matches[0])}.`, selected: matches[0] }
		}
		const candidates = matches.slice(0, SHOWN)
		const text =
			matches.length > SHOWN
				? `I found ${matches.length} patients matching “${cleaned}”, here are the first ${SHOWN}. Add more (like a time) to narrow it down.`
				: `I found ${matches.length} patients matching “${cleaned}”, which one?`
		return { text, candidates }
	}

	// Picking from the chooser selects the patient. If the chooser came from a
	// one-sentence question ("…for Patient_20…" that was ambiguous), run that question
	// against the chosen patient too, so the user doesn't have to ask twice.
	const choosePatient = async (patient, pendingQuestion) => {
		const resolved = await attachPatient(patient)
		if (pendingQuestion && resolved?.referenceEndMs) {
			await runQuery(pendingQuestion, resolved, [])
		}
	}

	// Fire the question at clara against a SPECIFIC set of patient ids and render the
	// reply. Split out from send() so a one-sentence "…for Patient_X" question can run
	// immediately with the ids just resolved (rather than waiting a render for state).
	const runQuery = async (question, ids, history) => {
		setLoading(true)
		try {
			const response = await fetchWithTimeout(`${CLARA_API}/query`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({
					question,
					history: history.slice(-3),
					workspace_name: selectedWorkspace,
					// Null, not zero, when this chat has no patient. Number("") is 0, and 0 is a
					// real epoch millisecond, so the server would have resolved "the past 4
					// hours" against January 1970 and answered that it had no data.
					patient_id: ids.patientId ? Number(ids.patientId) : null,
					data_object_id: ids.dataObjectId ? Number(ids.dataObjectId) : null,
					reference_end_ms: ids.referenceEndMs ? Number(ids.referenceEndMs) : null,
					// Where the archive BEGINS. Without it a clock window that falls before the
					// recording ever started comes back as "I don't have that data", which reads
					// as a fact about the patient rather than about the window.
					...(selectedRecording?.start ? { recording_start_ms: Number(selectedRecording.start) } : {}),
					// Without a zone Clara refuses clock questions rather than guess an hour.
					// The RECORDING's timezone, as Data Review uses, so "0400" means 04:00 where
					// the patient is. The browser's zone only when no recording is attached.
					client_timezone: (await fetchRecordingTimeZone(endpointProvider, LINKS, ids.dataObjectId)) || clientTimezone(),
					conversation_id: conversationIdRef.current,
				}),
			})
			const data = await response.json()
			const structuredQuery = data.structured_query
			if (structuredQuery?.mode === "select_patient") {
				// Clara classified a "watch patient X" request — do the match here.
				const { text, candidates, selected } = await resolvePatientSelection(structuredQuery.patient_query)
				if (selected) {
					// attachPatient announces the patient and the archive itself, and starts a
					// new chat when this one already belongs to someone else.
					await attachPatient(selected)
				} else {
					setMessages(prev => [...prev, { who: "clara", text, candidates }])
				}
				if (speakAnswers) {
					speak(text)
				}
			} else {
				// The widened no-patient gate lets any "watch/select …" phrasing through to
				// the server; when one turns out to be a data question with no patient
				// attached, the server errors on the missing ids. Nudge instead of "(no
				// answer)".
				const noPatientFallback =
					!patientReady && !data.answer
						? "Pick a patient first, or tell me which one to watch, like “watch patient 1234”."
						: null

				// Routing is the server's job now: /query decides between the patient's
				// vitals, the site's documents, both, or a refusal, and returns sources
				// when the answer rests on a document.
				setMessages(prev => [...prev, { who: "clara", text: data.answer ?? noPatientFallback ?? "(no answer)", query: structuredQuery, result: data.result, view: data.view, sources: data.sources, headline: data.headline, headlines: data.headlines, steps: data.steps }])
				if (speakAnswers && data.answer) {
					speak(data.answer)
				}
			}
		} catch (error) {
			setMessages(prev => [...prev, { who: "clara", text: isTimeout(error) ? CLARA_TIMEOUT_MESSAGE : "Couldn’t reach Clara.", query: null }])
		} finally {
			setLoading(false)
			// The backend saved this turn; refresh the rail and mark this chat active.
			setActiveConversationId(conversationIdRef.current)
			fetchConversations()
		}
	}

	const send = async (override) => {
		const question = (typeof override === "string" ? override : input).trim()
		if (!question || loading) {
			return
		}
		heldRef.current = false
		// Recent turns, so a follow-up can be resolved against them. Built from the
		// current messages before we append this turn.
		const history = conversationHistory(messages)

		// One-sentence select + ask: if the question names a patient, resolve them first
		// so the SAME question is answered against that patient — no separate "watch" turn.
		const ref = extractPatientRef(question)
		if (ref) {
			const { candidates, selected } = await resolvePatientSelection(ref.term)
			if (selected) {
				onStart?.()
				setMessages(prev => [...prev, { who: "user", text: question }])
				setInput("")
				const resolved = (await attachPatient(selected)) || {}
				if (!resolved.referenceEndMs) {
					setMessages(prev => [...prev, { who: "clara", text: "I selected that patient, but couldn’t load their recording window — try picking them from the dropdown.", query: null }])
					return
				}
				await runQuery(question, resolved, history)
				return
			}
			if (candidates) {
				// Ambiguous — show a chooser and stash the question to run once they pick.
				onStart?.()
				setMessages(prev => [
					...prev,
					{ who: "user", text: question },
					{ who: "clara", text: `I found multiple patients matching “${ref.term}” — which one?`, candidates, pendingQuestion: question },
				])
				setInput("")
				return
			}
			if (ref.strong) {
				// A clear "Patient_…" token that matched nobody — say so.
				onStart?.()
				setMessages(prev => [
					...prev,
					{ who: "user", text: question },
					{ who: "clara", text: `I couldn’t find a patient matching “${ref.term}”.`, query: null },
				])
				setInput("")
				return
			}
			// Weak ref that matched nothing → fall through and treat as an ordinary question.
		}

		// A chat with no patient is a normal chat now, not a broken one: document
		// questions need no patient, and the picker offers to skip for exactly that.
		//
		// This used to guess client side which questions were allowed through, from a
		// regex that recognised "watch …" and greetings and nothing else. So "Who is
		// allowed to inject chemotherapy into an Ommaya reservoir?", asked straight after
		// clicking Skip, was answered "Pick a patient first" by a rule that had never
		// seen the question. The server routes documents before it looks for a patient,
		// so the question goes there and the server decides. A genuine vitals question
		// with no patient comes back as the same nudge, from the side that can tell.
		onStart?.()
		setMessages(prev => [...prev, { who: "user", text: question }])
		setInput("")
		await runQuery(question, { patientId, dataObjectId, referenceEndMs }, history)
	}
	// Keep the latest send reachable from the recorder's onstop callback (auto-send).
	sendRef.current = send

	// Keep the page's rail in step. Functions are included so the rail can start a chat
	// or open one without the page duplicating that logic.
	const latestSourcesForRail = [...messages].reverse().find(m => m.sources?.length)?.sources ?? []
	// Label each chat by its patient. A chat is about one patient now, so the patient is
	// the useful name for it; the first question stays as the second line, because that
	// is what tells two chats about the same patient apart.
	const labels = readPatientLabels()
	const titledConversations = conversations.map(conversation => ({
		...conversation,
		patient_label: labels[String(conversation.patient_id)] || "",
	}))
	/* The rail's handlers are STABLE wrappers that call through a ref.

	   They used to be the functions themselves, handed over inside an effect whose
	   dependencies were the data, not the functions. So the rail kept whichever closures
	   the last effect run happened to create, and every piece of state those closures had
	   captured was frozen at that moment. That is what broke switching chats: the closure
	   the rail was left holding believed a load was still in progress.

	   Wrappers created once, reading the latest implementations out of a ref, mean the
	   rail always calls current code no matter when the effect last ran. */
	handlersRef.current = { newChat, openConversation }
	useEffect(() => {
		onRailData?.({
			conversations: titledConversations,
			activeId: activeConversationId,
			sources: latestSourcesForRail,
			...railHandlers,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conversations, activeConversationId, messages])

	// The archive being read, said out loud. It decides what "the past 4 hours" and
	// "yesterday" are measured from, and its real length is worth seeing: one recording
	// in testing announced itself as "covers 26 Jan 2026" and held 2 minutes 44 seconds.
	const recordingLabel = describeRecording(selectedRecording, recordingTimeZone)

	// The row the page is actually watching. Looking it up in a local list only worked
	// while that list was assumed to hold every patient.
	const currentPatient =
		selectedPatient && String(selectedPatient.patient_key) === String(patientId)
			? selectedPatient
			: patients.find(patient => String(patient.patient_key) === String(patientId))
	const hasText = input.trim().length > 0

	// Sources from the newest answer that has any: the rail shows the evidence for what
	// is on screen, and keeps showing it while the clinician reads.
	const latestSources = [...messages].reverse().find(m => m.sources?.length)?.sources ?? []

	if (!CLARA_API) {
		return <div className={styles.chat}><div className={styles.messages}>Clara is not configured.</div></div>
	}

	return (
		<div className={styles.chat}>
			<div className={styles.messages} ref={messagesRef} onWheel={event => event.deltaY < 0 && hold()} onTouchMove={hold} onPointerDown={event => event.target === event.currentTarget && hold()}>
				{messages.map((message, index) =>
					message.who === "user" ? (
						<div key={index} className={styles.userRow}>
							<div className={styles.userColumn}>
								<div className={styles.userBubble}>{message.text}</div>
								<CopyButton text={message.text} />
							</div>
						</div>
					) : (
						<div key={index} className={`${styles.claraRow} ${!loading && index === messages.length - 1 && !message.historical ? styles.latestTurn : ""}`}>
							<div className={styles.claraReply}>
								{message.steps?.length > 0 && (
									<ul className={styles.answerSteps} aria-label="How I got this">
										{message.steps.map((step, stepIndex) => (
											<li
												key={step}
												className={`${styles.answerStep} ${message === building ? styles.revealIn : ""}`}
												style={message === building ? { animationDelay: `${revealDelays(message).steps[stepIndex]}ms` } : undefined}
											>
												<svg className={styles.answerStepCheck} viewBox="0 0 24 24" aria-hidden="true">
													<circle cx="12" cy="12" r="11" />
													<path d="M7 12.5l3.2 3.2L17 9" />
												</svg>
												<span>{boldFigures(step)}</span>
											</li>
										))}
									</ul>
								)}
								{message.headline && (
									<div
										className={`${styles.headline} ${message === building ? styles.revealIn : ""}`}
										style={message === building ? { animationDelay: `${revealDelays(message).headline}ms` } : undefined}
									>
										<div className={styles.headlineLead}>{message.headline.lead}</div>
										<div className={styles.headlineFigure}>
											<span className={styles.headlineValue}>
												{message.headline.value}
												{message.headline.unit && (
													<span className={styles.headlineUnit}>{message.headline.unit === "%" ? "%" : ` ${message.headline.unit}`}</span>
												)}
											</span>
											{message.headline.context && <span className={styles.headlineContext}>{message.headline.context}</span>}
										</div>
									</div>
								)}
								{message.headlines?.length > 0 && (
									<div
										className={`${styles.headlineRow} ${message === building ? styles.revealIn : ""}`}
										style={message === building ? { animationDelay: `${revealDelays(message).headline}ms` } : undefined}
									>
										{message.headlines.map(figure => (
											<div key={figure.lead} className={styles.headline}>
												<div className={styles.headlineLead}>{figure.lead}</div>
												<div className={styles.headlineFigure}>
													<span className={styles.headlineValue}>
														{figure.value}
														{figure.unit && <span className={styles.headlineUnit}>{figure.unit === "%" ? "%" : ` ${figure.unit}`}</span>}
													</span>
													{figure.context && <span className={styles.headlineContext}>{figure.context}</span>}
												</div>
											</div>
										))}
									</div>
								)}
								{shows(message, 1) && (
									<div className={`${styles.answerText} ${message.headline || message.headlines?.length ? styles.answerTextUnder : ""}`}>
										<LongAnswer done={streamedIds.has(index) || message.historical || index !== messages.length - 1}>
											<StreamingAnswer
												text={message.text}
												animate={message === building}
												onDone={() => {
													setStreamedIds(prev => (prev.has(index) ? prev : new Set(prev).add(index)))
													setReveal(prev => (prev.message === message && prev.stage === 1 ? { ...prev, stage: 2 } : prev))
												}}
											/>
										</LongAnswer>
									</div>
								)}
								{message.candidates && shows(message, 3) && (
									<div className={styles.patientChoices}>
										{message.candidates.map(patient => (
											<button key={patient.patient_key} type="button" className={styles.patientChoice} onClick={() => choosePatient(patient, message.pendingQuestion)}>
												{displayOf(patient)}
											</button>
										))}
									</div>
								)}
								{message.query && interpChip(message.query) && !drawsChart(message, index === messages.length - 1) && shows(message, 3) && (
									<div className={styles.interp}>{interpChip(message.query)}</div>
								)}
								{message.sources?.length > 0 && (streamedIds.has(index) || message.historical || index !== messages.length - 1) && shows(message, 3) && (
									<div className={`${styles.measure} ${message === building ? styles.revealIn : ""}`}>
										<ContextCards sources={message.sources} />
									</div>
								)}
								{(() => {
									// A summary ("what was the MAP") has no threshold to draw — the
									// compute path uses a 1e9 sentinel to suppress episodes, which we
									// must NOT render as a reference line (it blows up the y-range).
									// Trust the structured mode, not result.query.mode (that echoes the
									// sentinel "above" call).
									const isSummary = message.query?.mode === "summary"
									const lines = viewLines(message.view) ?? (isSummary ? [] : thresholdsOf(message.result?.query))
									const result = message.result ?? viewResult(message.view)
									const several = viewSeries(message.view)
									const svgChart = several.length ? (
										<div className={styles.chartStack}>
											{several.map(item => (
												<div key={item.signal}>
													<div className={styles.chartSeriesName}>{item.label}</div>
													<ClaraTrendChart
														trend={item.result.trend}
														units={item.result.unit}
														windowStart={item.result.query.start_time}
														windowEnd={item.result.query.end_time}
														timeZone={recordingTimeZone}
														height={SERIES_HEIGHT}
													/>
												</div>
											))}
										</div>
									) : result?.trend && result.trend.length > 1 && (
										<ClaraTrendChart
											trend={result.trend}
											episodes={result.episodes}
											crossings={result.crossings}
											thresholds={lines}
											units={result.unit}
											windowStart={result.query?.start_time}
											windowEnd={result.query?.end_time}
											timeZone={recordingTimeZone}
										/>
									)
									// Latest answer → real Data Review graph (SVG fallback on error).
									// Older answers → SVG (the embed is single-instance / global state).
									const latest = index === messages.length - 1 && !message.historical
									const span = message.view?.window
									const chart = (latest && message.view?.series?.length > 1) ? (
										<GraphErrorBoundary key={`boundary-${index}-several`} fallback={svgChart}>
											<ClaraGraphEmbed
												patientId={patientId}
												dataObjectId={dataObjectId}
												referenceEndMs={span?.end_ms || referenceEndMs}
												series={message.view.series}
												thresholds={lines}
												windowHours={(Number(span?.end_ms) - Number(span?.start_ms)) / 3600000}
												fallback={svgChart}
											/>
										</GraphErrorBoundary>
									) : (latest && message.result?.query) ? (
										<GraphErrorBoundary key={`boundary-${index}-${message.result.query.modality}`} fallback={svgChart}>
											<ClaraGraphEmbed
												patientId={patientId}
												dataObjectId={dataObjectId}
												referenceEndMs={message.result.query.end_time || referenceEndMs}
												modality={message.result.query.modality}
												thresholds={lines}
												valueMin={message.result.stats?.min}
												valueMax={message.result.stats?.max}
												windowHours={(Number(message.result.query.end_time) - Number(message.result.query.start_time)) / 3600000}
												episodes={message.result.episodes}
												archiveLabel={message.result.query.archive_label}
												fallback={svgChart}
											/>
										</GraphErrorBoundary>
									) : svgChart
									if (!chart || !shows(message, 3)) {
										return null
									}
									const chartProps = {
										trend: result?.trend,
										episodes: result?.episodes,
										crossings: result?.crossings,
										thresholds: lines,
										units: result?.unit,
										windowStart: result?.query?.start_time,
										windowEnd: result?.query?.end_time,
										timeZone: recordingTimeZone,
									}
									return (
										<figure className={`${styles.chartCard} ${message === building ? styles.revealIn : ""}`}>
											{interpChip(message.query) && <figcaption className={styles.chartCaption}>{interpChip(message.query)}</figcaption>}
											{chart}
											<ChartActions
												chartProps={chartProps}
												charts={several.map(item => ({ label: item.label, chartProps: { trend: item.result.trend, units: item.result.unit, windowStart: item.result.query.start_time, windowEnd: item.result.query.end_time, timeZone: recordingTimeZone } }))}
												label={interpChip(message.query) || "Clara chart"}
											/>
										</figure>
									)
								})()}
								<CopyButton text={message.text} />
							</div>
						</div>
					)
				)}

				{loading && (
					<div className={`${styles.claraRow} ${styles.latestTurn}`}>
						<div className={styles.claraReply}>
							<div className={styles.working} aria-live="polite">
								<ClaraOrb />
								{WORKING_LABEL}
							</div>
						</div>
					</div>
				)}
				<div ref={endRef} />
			</div>

			<div className={styles.inputBar}>
				{/* The suggestion chips lived here and took a band of the screen on every
				    question to say the same nine things. They are examples, which is a thing
				    you read once: they are in the guide now, where each one can be copied. */}
				<div className={styles.composer}>
					<input
						className={styles.composerInput}
						value={input}
						maxLength={MAX_CHARS}
						onChange={event => setInput(event.target.value)}
						onKeyDown={event => event.key === "Enter" && send()}
						placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Write a message…"}
					/>
					<div className={styles.composerRow}>
						{/* Bottom-left: patient picker "attached" to the composer, with the
						    Talk back toggle beside it. */}
						<div className={styles.composerLeft}>
							{/* Who this chat is about, attached to the box you type in. It reads as
							    part of the question you are about to ask, which is what it is. The
							    name carries the recording's dates beside it, because those are what
							    "the past 4 hours" and "yesterday" get measured from; the archive's
							    file name is thirty characters of machine id and stays in the tooltip. */}
							{currentPatient ? (
								<>
									<span className={styles.patientChip} title={recordingLabel}>
										<span className={styles.patientAvatar} aria-hidden="true">
											<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
												<circle cx="12" cy="7" r="4" />
											</svg>
										</span>
										<span className={styles.patientChipName}>{displayOf(currentPatient)}</span>
										{recordingSpan(selectedRecording, recordingTimeZone) && (
											<span className={styles.patientChipMeta}>{recordingSpan(selectedRecording, recordingTimeZone)}</span>
										)}
									</span>
									<button
										type="button"
										className={styles.patientSwitch}
										onClick={() => setPickerOpen(true)}
										disabled={loading}
										title="Start a new chat about a different patient. This chat keeps its own."
									>
										Switch patient (New chat)
									</button>
								</>
							) : (
								<button
									type="button"
									className={styles.patientPick}
									onClick={() => setPickerOpen(true)}
									disabled={loading}
									title="Attach a patient to this chat"
								>
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<path d="M12 5v14M5 12h14" />
									</svg>
									Select patient
								</button>
							)}
							{/* Talk back: speak each answer aloud. A real switch rather than a
							    checkbox in a pill, because it is a mode that stays on, and it reads
							    at a glance from across a bed space. Turning it off mid answer also
							    cuts off whatever is being spoken, which is what you want when you
							    flip it off because someone walked into the room. */}
							<button
								type="button"
								role="switch"
								aria-checked={speakAnswers}
								className={`${styles.talkBack} ${speakAnswers ? styles.talkBackOn : ""}`}
								onClick={() => {
									const on = !speakAnswers
									setSpeakAnswers(on)
									if (!on) {
										stopSpeaking()
									}
								}}
								title={speakAnswers ? "Clara speaks her answers aloud" : "Clara answers in text only"}
							>
								<span className={styles.talkBackTrack} aria-hidden="true">
									<span className={styles.talkBackKnob} />
								</span>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
									<path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
									{speakAnswers && <path d="M19.07 4.93a9 9 0 0 1 0 14.14" />}
								</svg>
								Talk back
							</button>
						</div>

						<div className={styles.composerActions}>
							{/* Live character count of the message box (warns near the cap). */}
							<span className={`${styles.charCount} ${input.length >= MAX_CHARS ? styles.charCountFull : ""}`}>
								{input.length}/{MAX_CHARS}
							</span>
							{/* Voice: outlined pill, mic icon. Red "Stop" while recording. */}
							<button
								type="button"
								className={`${styles.voiceButton} ${recording ? styles.voiceButtonRecording : ""}`}
								onClick={toggleRecording}
								disabled={loading || transcribing}
								aria-label={recording ? "Stop recording" : "Record a question"}
								title={recording ? "Click to stop" : "Click to record"}
							>
								{recording ? (
									/* A square, the universal stop, rather than the word. */
									<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
										<rect x="7" y="7" width="10" height="10" rx="2" />
									</svg>
								) : (
									<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
										<path d="M19 11a7 7 0 0 1-14 0M12 18v3" />
									</svg>
								)}
							</button>

							{/* Send: filled brand pill, up-arrow. Active once there's text. */}
							<button
								type="button"
								className={styles.sendButton}
								onClick={() => send()}
								disabled={!hasText || loading}
								aria-label="Send"
								title="Send"
							>
								<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M12 19V5M5 12l7-7 7 7" />
								</svg>
							</button>
						</div>
					</div>
				</div>
				<p className={styles.disclaimer}>
					Clara can make mistakes. Always verify against the patient’s data before making clinical decisions.
				</p>
			</div>

			{loading && <div className={styles.bottomGlow} />}

			<ClaraPatientModal
				open={pickerOpen}
				selectedKey={patientId}
				onSelect={attachPatient}
				onSkip={patientId ? null : () => setPickerOpen(false)}
				onClose={() => setPickerOpen(false)}
			/>
		</div>
	)
}
